import { useMemo, useState } from 'react';
import { outboundAPI } from '../api/client';
import type { ClawerContact } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { TranslationPill } from './TranslationPill';
import { VoiceRecorder } from './VoiceRecorder';

interface ScheduledMessageComposerProps {
  contacts: ClawerContact[];
  onCreated?: () => void;
  prefillContactId?: number;
  prefillContent?: string;
  userLanguage?: string;
}

function defaultScheduleLocal(): string {
  const date = new Date(Date.now() + 15 * 60 * 1000);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function ScheduledMessageComposer({ contacts, onCreated, prefillContactId, prefillContent, userLanguage }: ScheduledMessageComposerProps) {
  const [contactId, setContactId] = useState<number | null>(prefillContactId || (contacts[0]?.id ?? null));
  const [content, setContent] = useState(prefillContent || '');
  const [sendAtLocal, setSendAtLocal] = useState(defaultScheduleLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedContact = contacts.find(c => c.id === contactId);
  const {
    hasLanguageMismatch, translateEnabled, setTranslateEnabled,
    preview, loading: translating, getTranslationPreview, clearPreview, targetLanguage,
  } = useTranslation({
    userLanguage: userLanguage || 'en',
    contactLanguage: selectedContact?.language || null,
  });

  const handleVoiceTranscribed = (text: string) => {
    setContent(prev => prev ? `${prev} ${text}` : text);
  };

  const contactOptions = useMemo(
    () => contacts.map((contact) => ({
      id: contact.id,
      label: contact.display_name || contact.push_name || contact.phone,
    })),
    [contacts],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!contactId || !content.trim()) return;

    try {
      setBusy(true);
      setError(null);

      // If translation enabled, get fresh translation for final send
      let finalContent = content.trim();
      let originalText: string | undefined;
      let wasTranslated = false;
      if (translateEnabled && hasLanguageMismatch) {
        const translated = await getTranslationPreview(content);
        if (translated) {
          originalText = finalContent;
          finalContent = translated;
          wasTranslated = true;
        }
      }

      await outboundAPI.createScheduled({
        contact_id: contactId,
        content: finalContent,
        send_at: new Date(sendAtLocal).toISOString(),
        ...(wasTranslated && { original_text: originalText, was_translated: true }),
      });
      setContent('');
      clearPreview();
      setSendAtLocal(defaultScheduleLocal());
      onCreated?.();
    } catch (err) {
      console.error('Failed to schedule outbound message:', err);
      setError('Failed to schedule message');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-edge bg-white p-3 space-y-3">
      <h3 className="text-sm font-semibold text-ink" data-ph-no-mask>Schedule Message</h3>
      <p className="text-xs text-ink-secondary" data-ph-no-mask>
        Manual scheduled sends bypass trust level but still respect outbound safety gates.
      </p>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-ink-secondary" data-ph-no-mask>Contact</label>
        <select
          value={contactId ?? ''}
          onChange={(e) => setContactId(Number(e.target.value))}
          className="w-full rounded-lg border border-edge px-3 py-2 text-sm"
        >
          {contactOptions.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-ink-secondary" data-ph-no-mask>Message</label>
        <div className="flex gap-2">
          <textarea
            value={content}
            onChange={(e) => { setContent(e.target.value); clearPreview(); }}
            rows={3}
            placeholder="Write your outbound message"
            className="flex-1 rounded-lg border border-edge px-3 py-2 text-sm"
          />
          <div className="flex flex-col justify-end">
            <VoiceRecorder onTranscribed={handleVoiceTranscribed} />
          </div>
        </div>

        {/* Translation controls */}
        {hasLanguageMismatch && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TranslationPill
                targetLanguage={targetLanguage}
                enabled={translateEnabled}
                onToggle={setTranslateEnabled}
              />
              {translateEnabled && content.trim() && (
                <button
                  type="button"
                  onClick={() => getTranslationPreview(content)}
                  className="text-xs text-accent/60 hover:text-accent"
                  disabled={translating}
                >
                  {translating ? 'Translating...' : 'Preview translation'}
                </button>
              )}
            </div>
            {preview && (
              <div className="rounded-lg bg-accent/5 border border-accent/10 px-3 py-2 text-sm text-accent/80">
                {preview}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-ink-secondary" data-ph-no-mask>Send at</label>
        <input
          type="datetime-local"
          value={sendAtLocal}
          onChange={(e) => setSendAtLocal(e.target.value)}
          className="w-full rounded-lg border border-edge px-3 py-2 text-sm"
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={busy || !contactId || !content.trim()}
        className="w-full rounded-lg bg-ink text-white text-sm py-2 disabled:opacity-50"
        data-ph-no-mask
      >
        {busy ? 'Scheduling...' : 'Schedule message'}
      </button>
    </form>
  );
}

export default ScheduledMessageComposer;
